import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import {
  fadeInScaleVariants,
  interactiveHoverVariants,
  pulseVariants,
  viewportOnce,
} from '@/animations';
import { Button } from '@/components';
import { formatCurrency } from '@/config';
import { PRODUCTS_CONTENT, SHOP_CONTENT } from '@/content';
import { useMagnetic } from '@/hooks/useMagnetic';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';
import type { Product } from '@/types';
import { playAudio } from '@/utils/audio';
import { Icon } from '@/utils/icons';
import { createSparkles } from '@/utils/sparkles';

import LazyImage from './LazyImage';

const QuickViewModal = React.lazy(() => import('./QuickViewModal'));

interface ProductCardProps {
  product: Product;
  priority?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, priority = false }) => {
  const addToCart = useCartStore(state => state.addToCart);
  const addToWishlist = useWishlistStore(state => state.addToWishlist);
  const removeFromWishlist = useWishlistStore(state => state.removeFromWishlist);
  const isInWishlist = useWishlistStore(state => state.isInWishlist);

  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [showHeartOverlay, setShowHeartOverlay] = useState(false);
  const [lastTap, setLastTap] = useState(0);
  const [tiltStyle, setTiltStyle] = useState<React.CSSProperties>({});

  const wishlistRef = useMagnetic<HTMLButtonElement>(0.3, 40);
  const quickViewRef = useMagnetic<HTMLButtonElement>(0.3, 40);

  const isFavorite = useMemo(() => isInWishlist(product.id), [isInWishlist, product.id]);

  const liveViewers = useMemo(() => {
    const sum = product.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (sum % 17) + 5;
  }, [product.id]);

  const lowStockThreshold = 4;
  const stockCount = useMemo(() => {
    if (!product.inStock) return 0;
    const sum = product.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return (sum % 8) + 1;
  }, [product.id, product.inStock]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCoords({ x, y });

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const percentX = (x - centerX) / centerX;
    const percentY = (y - centerY) / centerY;

    const maxRotation = 8;
    const rx = -percentY * maxRotation;
    const ry = percentX * maxRotation;

    setTiltStyle({
      transform: `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg)`,
      transition: 'transform 0.08s ease-out',
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTiltStyle({
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
      transition: 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
    });
  }, []);

  const handleDoubleTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const now = Date.now();
      const DOUBLE_PRESS_DELAY = 300;
      if (now - lastTap < DOUBLE_PRESS_DELAY) {
        e.preventDefault();
        e.stopPropagation();

        const clientX =
          'touches' in e && e.touches[0] ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY =
          'touches' in e && e.touches[0] ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        createSparkles(clientX, clientY);

        if (isFavorite) {
          void removeFromWishlist(product.id);
        } else {
          void addToWishlist(product);
          playAudio.playHeart();
        }
        setShowHeartOverlay(true);
        setTimeout(() => setShowHeartOverlay(false), 800);
        setLastTap(0);
      } else {
        setLastTap(now);
      }
    },
    [lastTap, isFavorite, product, addToWishlist, removeFromWishlist]
  );

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (product.inStock) {
        createSparkles(e.clientX, e.clientY);
        void addToCart({ ...product, quantity: 1 });
        playAudio.playPop();
      }
    },
    [addToCart, product]
  );

  const toggleWishlist = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      createSparkles(e.clientX, e.clientY);
      if (isFavorite) {
        void removeFromWishlist(product.id);
      } else {
        void addToWishlist(product);
        playAudio.playHeart();
      }
    },
    [isFavorite, product, removeFromWishlist, addToWishlist]
  );

  const badge = useMemo(() => {
    if (!product.inStock) return { text: PRODUCTS_CONTENT.labels.outOfStock, type: 'out' };
    if (product.isSale) return { text: PRODUCTS_CONTENT.labels.sale, type: 'sale' };
    if (product.isNew) return { text: PRODUCTS_CONTENT.labels.new, type: 'new' };
    return null;
  }, [product.inStock, product.isSale, product.isNew]);

  const badgeClass = useMemo(() => {
    switch (badge?.type) {
      case 'new':
        return 'badge-new';
      case 'sale':
        return 'badge-sale';
      case 'out':
        return 'badge-out';
      default:
        return '';
    }
  }, [badge?.type]);

  return (
    <>
      <motion.div
        layout
        initial="initial"
        whileInView="animate"
        viewport={viewportOnce}
        variants={fadeInScaleVariants}
        className="group relative h-full">
        <div
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={tiltStyle}
          className="toy-card relative flex h-full flex-col overflow-hidden transition-shadow duration-300 group-hover:shadow-2xl">
          <div
            className="pointer-events-none absolute -inset-px z-30 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{
              background: `radial-gradient(300px circle at ${coords.x}px ${coords.y}px, rgba(106, 160, 212, 0.22), transparent 80%)`,
            }}
          />

          <div className="relative z-10 aspect-[1/1] overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100">
            <Link
              to={`/product/${product.id}`}
              onClick={handleDoubleTap}
              className="block h-full w-full"
              aria-label={product.name}>
              {product.thumbnail ? (
                <LazyImage
                  src={product.thumbnail}
                  alt={product.name}
                  priority={priority}
                  className="h-full w-full"
                  imgClassName="transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                />
              ) : (
                <div className="skeleton flex h-full w-full items-center justify-center">
                  <motion.div
                    variants={pulseVariants}
                    animate="animate"
                    className="text-4xl opacity-20">
                    <Icon name={SHOP_CONTENT.empty.icon} size={54} className="text-primary-700" />
                  </motion.div>
                </div>
              )}

              <AnimatePresence>
                {showHeartOverlay && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0, y: 10 }}
                    animate={{ scale: [0, 1.4, 1], opacity: [0, 0.9, 0.9, 0], y: [10, -15, -25] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-white/20 text-rose-500 shadow-lg backdrop-blur-md">
                      <Icon name="Heart" size={28} fill="currentColor" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </Link>

            <AnimatePresence>
              {badge && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className={`absolute top-4 left-4 z-10 ${badgeClass} shadow-sm`}>
                  {badge.text}
                </motion.span>
              )}
            </AnimatePresence>

            <motion.button
              ref={wishlistRef}
              onClick={toggleWishlist}
              aria-label={
                isFavorite
                  ? PRODUCTS_CONTENT.card.removeFromWishlist
                  : PRODUCTS_CONTENT.card.addToWishlist
              }
              className={`shadow-interactive absolute top-4 right-4 z-10 flex h-12 w-12 items-center justify-center rounded-2xl backdrop-blur-md transition-all duration-300 ${
                isFavorite
                  ? 'from-primary-500 to-primary-600 bg-gradient-to-br text-white shadow-lg'
                  : 'hover:text-primary-500 bg-white/90 text-neutral-600 hover:bg-white hover:shadow-lg'
              }`}
              whileHover="hover"
              whileTap="tap"
              variants={interactiveHoverVariants}>
              <Icon
                name="Heart"
                size={20}
                fill={isFavorite ? 'currentColor' : 'none'}
                className={isFavorite ? 'animate-bounce-soft' : ''}
              />
            </motion.button>

            <div className="absolute inset-x-0 bottom-0 z-10 translate-y-full p-3 transition-transform duration-500 ease-out group-hover:translate-y-0">
              <div className="flex gap-2">
                <Button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  variant="primary"
                  size="md"
                  leftIcon={<Icon name="ShoppingBag" size={18} />}
                  className={`h-12 flex-1 shadow-lg ${!product.inStock ? 'cursor-not-allowed opacity-50 grayscale' : ''}`}>
                  <span className="hidden sm:inline">
                    {product.inStock
                      ? PRODUCTS_CONTENT.card.addToCartLabel
                      : PRODUCTS_CONTENT.card.outOfStockLabel}
                  </span>
                </Button>
                <Button
                  ref={quickViewRef}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsQuickViewOpen(true);
                  }}
                  variant="secondary"
                  size="md"
                  isIconOnly={true}
                  className="h-12 w-12 shadow-lg"
                  aria-label="Quick view">
                  <Icon name="Eye" size={18} />
                </Button>
              </div>
            </div>
          </div>

          <div className="z-10 flex flex-1 flex-col px-4 py-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="fill-secondary-400 text-secondary-400" />
                  <span className="font-display text-sm font-bold text-neutral-800">
                    {product.rating}
                  </span>
                </div>
                <span className="text-xs font-medium text-neutral-500">
                  ({product.reviewCount})
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs font-bold tracking-wider text-neutral-500 uppercase">
                  {product.category}
                </span>
                {product.model_number && (
                  <span className="font-body text-xs font-bold text-neutral-400">
                    #{product.model_number}
                  </span>
                )}
              </div>
            </div>

            <Link to={`/product/${product.id}`} className="group mb-2 block">
              <h3 className="font-display group-hover:text-primary-600 line-clamp-2 text-base leading-tight font-bold text-neutral-800 capitalize transition-colors duration-200">
                {product.name}
              </h3>
            </Link>

            <div className="flex flex-col items-baseline gap-1">
              <span className="text-premium-gradient font-display text-xl leading-tight font-bold">
                {formatCurrency(product.price)}
              </span>
              {product.originalPrice && (
                <span className="decoration-primary-300/50 text-sm font-medium text-neutral-500 line-through">
                  {formatCurrency(product.originalPrice)}
                </span>
              )}
            </div>

            <div className="mt-auto flex items-center justify-between gap-2 border-t border-neutral-100/60 pt-2 text-[10px] font-bold dark:border-neutral-800/60">
              <div className="flex items-center gap-1.5 text-neutral-400 dark:text-neutral-500">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                <span>{liveViewers} shopping</span>
              </div>

              {stockCount <= lowStockThreshold && stockCount > 0 ? (
                <span className="animate-pulse-soft inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-2 py-0.5 font-black text-rose-600 dark:border-rose-900/30 dark:bg-rose-950/20 dark:text-rose-400">
                  Only {stockCount} left!
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </motion.div>

      <React.Suspense fallback={null}>
        <QuickViewModal
          product={product}
          isOpen={isQuickViewOpen}
          onClose={() => setIsQuickViewOpen(false)}
        />
      </React.Suspense>
    </>
  );
};

export default React.memo(ProductCard);
